import React, { createContext, useCallback, useMemo, useContext } from 'react';

import { useColorScheme } from '@mui/material/styles';

import { type PaletteMode } from './theme-mode';

export interface ThemeModeContextValue {
  mode: PaletteMode;
  toggleMode: (mode: PaletteMode | null) => void;
}

const ThemeModeContext = createContext<ThemeModeContextValue | null>(null);

export const useThemeMode = (): ThemeModeContextValue => {
  const value = useContext(ThemeModeContext);

  if (value === null) {
    throw new Error(
      'Fenrir: useThemeMode must be used within the context of ThemeModeProvider.',
    );
  }

  return value;
};

export function ThemeModeProvider({
  children,
  defaultMode,
}: React.PropsWithChildren<{ defaultMode: PaletteMode }>) {
  const { mode: _mode, setMode } = useColorScheme();

  const toggleMode = useCallback(
    // `mode` is saved to internal state and localStorage
    // If `mode` is null, it will be reset to the defaultMode
    (_mode: PaletteMode | null) => {
      setMode(_mode);
    },
    [setMode],
  );

  const value = useMemo(
    () => ({
      // retype for our use case
      // mode will never be system since we set a default mode every time
      // mode is undefined when it first loads; set to defaultMode
      mode: (_mode as PaletteMode | undefined) ?? defaultMode,
      toggleMode,
    }),
    [_mode, defaultMode, toggleMode],
  );

  return (
    <ThemeModeContext.Provider value={value}>
      {children}
    </ThemeModeContext.Provider>
  );
}
